import type { DsPick } from 'dragonmouse-shared/interfaces';
import type { DesignSystemContent } from './types';
import type { ColorTokenValue, DesignSystemSection, DesignSystemToken, DsLink } from '../types';
import { VALID_DESIGN_SYSTEM_SECTIONS } from '../types';

// Gets the display color string for a token (hex for color tokens, original value otherwise)
export function getColorString(token: DesignSystemToken): string {
	if (token.value.kind === 'color') {
		return (token.value as ColorTokenValue).hex;
	}
	return token.value.original;
}

// Splits tokens into base and per-mode groups based on token.mode.
// Tokens with mode undefined go to base; tokens with mode set go to that mode's array.
// returns Record with keys "base" and each modeId (e.g. { base: [], dark: [] })
export function groupTokensByTheme(
	tokens: DesignSystemToken[],
	modeIds: string[]
): Record<string, DesignSystemToken[]> {
	const result: Record<string, DesignSystemToken[]> = { base: [] };
	for (const id of modeIds) {
		result[id] = [];
	}

	for (const token of tokens) {
		if (token.mode && modeIds.includes(token.mode)) {
			result[token.mode].push(token);
		} else {
			result.base.push(token);
		}
	}

	return result;
}

// Strips --ds- or --theme- prefix from a CSS variable to get the byId key
function tokenIdFromCssVariable(cssVariable: string): string {
	return cssVariable.replace(/^--(ds|theme)-/, '');
}

// Strips .ds- prefix from a CSS class name to get the byId key
function recipeIdFromClassName(className: string): string {
	return className.replace(/^\.ds-/, '');
}

// ARCHITECTURE: The file path is the single source of truth for display names.
// Filenames use kebab-case matching the intended label (e.g. "button-variants.html" -> "Button Variants").
// All .title/.name fields on content types are initialized via this function so they can't diverge.
// LLM prompts enforce this convention; see ds_files_xml_json.md "Filename Naming Convention".
export function displayNameFromPath(path: string, section: DesignSystemSection): string {
	const parts = path.split('/');
	const filename = parts[parts.length - 1] ?? '';
	const withoutExt = filename.replace(/\.[^.]+$/, '');

	if (section === 'components') {
		// components/button/button.js -> dirname "button"
		const dirname = parts.length >= 2 ? parts[parts.length - 2] ?? withoutExt : withoutExt;
		return dirname
			.split(/[-_]/)
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(' ');
	}

	return withoutExt
		.split(/[-_]/)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(' ');
}

// Converts raw path entries (from backend XML) to DsLinks.
// Entries prefixed with "-" are deletions (deleted: true, no contentPath).
// For components: deduplicates by subdir; mixed updated+deleted = updated.
export function pathsToDsLinks(entries: string[], section: DesignSystemSection): DsLink[] {
	const parsed = entries.map((raw) => {
		const deleted = raw.startsWith('-');
		const path = deleted ? raw.slice(1) : raw;
		const label = displayNameFromPath(path, section);
		return { label, path, deleted };
	});

	if (section === 'components') {
		const bySubdir = new Map<string, { label: string; deleted: boolean }>();
		for (const entry of parsed) {
			const subdir = entry.path.split('/')[1];
			if (!subdir) continue;
			const existing = bySubdir.get(subdir);
			bySubdir.set(subdir, {
				label: entry.label,
				deleted: existing === undefined ? entry.deleted : existing.deleted && entry.deleted,
			});
		}
		const links: DsLink[] = [];
		for (const [dirname, { label, deleted }] of bySubdir) {
			links.push(deleted ? { label, deleted: true } : { label, contentPath: `ds-${dirname}` });
		}
		return links;
	}

	return parsed.map(({ label, path, deleted }) =>
		deleted ? { label, deleted: true } : { label, contentPath: path }
	);
}

// Resolves DsPicks to display names using design system content.
// returns Array of { pick, name } for rendering tiles
export function resolveDsPicksToDisplayNames(
	picks: DsPick[],
	content: DesignSystemContent | null
): Array<{ pick: DsPick; name: string }> {
	return picks.map((pick) => {
		if (pick.kind === 'token') {
			const id = tokenIdFromCssVariable(pick.cssVariable);
			const token = content?.theme?.byId?.[id] ?? content?.styles?.tokens?.byId?.[id];
			return { pick, name: token?.name ?? pick.cssVariable };
		}
		if (pick.kind === 'recipe') {
			const id = recipeIdFromClassName(pick.className);
			const recipe = content?.styles?.recipes?.byId?.[id];
			return { pick, name: recipe?.name ?? pick.className };
		}
		if (pick.kind === 'component') {
			const component = content?.components?.byId?.[pick.tagName];
			return { pick, name: component?.name ?? pick.tagName };
		}
		if (pick.kind === 'guideline') {
			const guideline = content?.guidelines?.find((g) => g.path === pick.guidelinePath);
			return { pick, name: guideline?.title ?? pick.guidelinePath };
		}
		if (pick.kind === 'gallery') {
			const gallery = content?.gallery?.find((g) => g.path === pick.galleryPath);
			return { pick, name: gallery?.title ?? pick.galleryPath };
		}
		if (pick.kind === 'asset') {
			const asset = content?.assets?.all?.find((a) => a.path === pick.assetPath);
			return { pick, name: asset?.name ?? pick.assetPath };
		}
		if (pick.kind === 'section') {
			return { pick, name: pick.section };
		}
		return { pick, name: String(pick) };
	});
}

// Generates a display name from a CSS class name / identifier
// ".ds-type-heading-lg" -> "Type Heading Lg"
// Strips DS prefixes (--ds-, --theme-, .ds-, ds-) then title-cases.
// File-path symbols fall through to displayNameFromPath
export function symbolDisplayName(sym: string): string {
	if (sym.includes('/')) {
		return displayNameFromPath(sym, sym.split('/')[0] as DesignSystemSection);
	}
	const stripped = sym.replace(/^--(ds|theme)-/, '').replace(/^\.?ds-/, '');
	return stripped
		.split('-')
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(' ');
}

// Maps a DsPick to its design system section and optional contentPath for navigation.
// returns { section, contentPath? } or null if pick kind is unknown
export function dsPickToNavTarget(
	pick: DsPick
): { section: DesignSystemSection; contentPath?: string } | null {
	switch (pick.kind) {
		case 'component':
			return { section: 'components', contentPath: pick.tagName };
		case 'guideline':
			return { section: 'guidelines', contentPath: pick.guidelinePath };
		case 'gallery':
			return { section: 'gallery', contentPath: pick.galleryPath };
		case 'token':
			return { section: 'theme' };
		case 'recipe':
			return { section: 'styles' };
		case 'asset':
			return { section: 'assets', contentPath: pick.assetPath };
		case 'section':
			if (!VALID_DESIGN_SYSTEM_SECTIONS.includes(pick.section as DesignSystemSection)) return null;
			return { section: pick.section as DesignSystemSection };
		default:
			return null;
	}
}

// Inverse of dsPickToNavTarget — converts a DsTab to the corresponding DsPick.
// Content-level tabs map to specific item variants; section-level tabs use the section variant.
export function dsTabToPick(tab: { section: DesignSystemSection; contentPath?: string }): DsPick {
	if (tab.contentPath) {
		switch (tab.section) {
			case 'components':
				return { kind: 'component', tagName: tab.contentPath };
			case 'guidelines':
				return { kind: 'guideline', guidelinePath: tab.contentPath };
			case 'gallery':
				return { kind: 'gallery', galleryPath: tab.contentPath };
			case 'assets':
				return { kind: 'asset', assetPath: tab.contentPath };
		}
	}
	return { kind: 'section', section: tab.section };
}
